// InfoBaumfaellung.js – Leistungsseite /baumfaellung: Hero, Fachtexte, Karussell, Verweise auf andere Leistungen.
import "./Home.css";
import "./custom.css";
import usePageMeta from "../../usePageMeta";
import { JsonLd, leistungSchema } from "../Strukturdaten";
import { Link } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Carousel from "react-bootstrap/Carousel";
import { karussellIntervall } from "../../reducedMotion";

import BildBuehne from "./images/Arbeiten/spezialfaellung-buehne.webp";
import BildKlettern from "./images/Arbeiten/faellung-skt.webp";
import BildRuecken from "./images/Raupe/holzruecken.webp";

// Pixelmaße der Dateien (Platzreservierung vor dem Laden)
const BILD_BUEHNE = { src: BildBuehne, breite: 1200, hoehe: 750 };
const BILD_KLETTERN = { src: BildKlettern, breite: 1200, hoehe: 750 };
const BILD_RUECKEN = { src: BildRuecken, breite: 1200, hoehe: 750 };

const facts = [
  {
    title: "Abtragen in Teilen",
    text: "Steht ein Baum zu dicht an Gebäuden, Straßen oder Leitungen, wird er nicht am Stück gefällt, sondern von oben nach unten zerlegt. Kronenteile und Stammstücke werden mit Seilen und Umlenkrollen kontrolliert abgelassen, so landet nichts, wo es nicht landen darf.",
  },
  {
    title: "Seilklettertechnik oder Arbeitsbühne",
    text: "Die Hubarbeitsbühne bietet einen sicheren Stand und erreicht Kronen bis in große Höhen, sofern der Untergrund sie trägt. Wo kein Fahrzeug hinkommt, in engen Gärten, Hanglagen oder im Bestand, arbeiten wir mit Seilklettertechnik direkt im Baum.",
  },
  {
    title: "Sturmschäden: Holz unter Spannung",
    text: "Angebrochene oder umgestürzte Bäume stehen oft unter hoher Spannung. Ein falscher Schnitt kann Stammteile unkontrolliert aufschlagen lassen. Solche Bäume werden zuerst gesichert und dann Schritt für Schritt entlastet.",
  },
  {
    title: "Fällzeiten nach § 39 BNatSchG",
    text: "Vom 1. März bis zum 30. September dürfen Bäume außerhalb des Waldes grundsätzlich nicht gefällt werden. Ausnahmen gibt es unter anderem für die Verkehrssicherheit. Viele Gemeinden haben zusätzlich eine Baumschutzsatzung. Wir sprechen das bei der Besichtigung an.",
  },
  {
    title: "Seilrücken: die Fläche wird nicht befahren",
    text: "Mit einer Forstseilwinde lässt sich Holz über 50 bis 150 Meter aus dem Bestand ziehen, ohne die dazwischenliegende Fläche zu befahren. Nur das Holz wird herangezogen, die Maschine bleibt auf der Rückegasse.",
  },
  {
    title: "Bodendruck: Raupe statt Forsttraktor",
    text: "Ein konventioneller Forsttraktor erzeugt Bodendrücke von 1,5 bis über 3 kg/cm². Der Geräteträger Moritz FR 75 kommt mit seinem Gummilaufwerk auf rund 0,28 kg/cm², ein Bruchteil davon.",
  },
];

// Strukturdaten dieser Leistung; nur Inhalte, die unten auf der Seite stehen
const schema = leistungSchema({
  pfad: "/baumfaellung",
  name: "Baumfällung",
  serviceType: "Gefahrbaum- und Spezialfällung",
  beschreibung:
    "Gefahrbaum- und Spezialfällung: Bäume, die neben Wohnhäusern, über Straßen und Leitungen, auf Friedhöfen oder im engen " +
    "Garten nicht am Stück fallen dürfen, werden mit Seilklettertechnik oder von der Hubarbeitsbühne von oben nach unten in " +
    "Teilen abgetragen und am Seil kontrolliert abgelassen. Sicherung von Sturmschäden, auf Wunsch auch kurzfristig; " +
    "Stubbenfräse; Holzrücken mit Forstseilwinde und funkgesteuertem Geräteträger Moritz FR 75 sowie Holzeinschlag mit " +
    "Rückung im Wald. Ob eine Genehmigung nötig ist und wann der richtige Zeitpunkt ist (§ 39 BNatSchG), klären wir bei der Besichtigung.",
  leistungen: [
    "Gefahrbaumfällung",
    "Spezialfällung: Abtragen in Teilen",
    "Sicherung von Sturmschäden",
    "Stubben fräsen",
    "Holzrücken mit Seilwinde und Raupe",
    "Holzeinschlag mit Rückung im Wald",
  ],
});

function InfoBaumfaellung() {
  usePageMeta({
    path: "/baumfaellung",
    title: "Baumfällung: Gefahrbaum- und Spezialfällung | Forst & Flur",
    description:
      "Gefahrbaum- und Spezialfällung in Schleswig-Holstein: Abtragen in Teilen mit Seilklettertechnik und Arbeitsbühne, Holzrücken mit Seilwinde. Jetzt anfragen.",
  });

  return (
    <div>
      <JsonLd daten={schema} />
      {/* Hero */}
      <div className="ff-page-hero">
        <div className="ff-page-hero__content">
          <span className="ff-eyebrow">Forst &amp; Flur</span>
          <h1 className="ff-page-hero__title">Baumfällung</h1>
          <p className="ff-page-hero__sub">
            Gefahrbäume sichern, Bäume in Teilen abtragen: kontrolliert, auch dort, wo es eng wird.
          </p>
        </div>
      </div>

      {/* Artikel-Body */}
      <div className="custom-dark-green text-white">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col xs={12} lg={10}>

              <h2 className="ff-article-h2">
                Gefahrbaum- und Spezialfällung:<br />wenn ein Baum nicht einfach fallen darf
              </h2>

              <p className="fs-5 text-shadow">
                Nicht jeder Baum lässt sich am Stück fällen. Neben Wohnhäusern, über Straßen und
                Leitungen, auf Friedhöfen oder im engen Garten fehlt der Platz, in dem ein Stamm
                sicher aufschlagen könnte. Dann wird der Baum von oben nach unten abgetragen:
                Ast für Ast, Stück für Stück, jedes Teil am Seil kontrolliert abgelassen.
              </p>

              <Row className="align-items-start my-4 gy-3">
                <Col xs={12} md={5}>
                  <img
                    src={BILD_BUEHNE.src}
                    width={BILD_BUEHNE.breite}
                    height={BILD_BUEHNE.hoehe}
                    alt="Spezialbaumfällung von der Hubarbeitsbühne: Kronenteile werden Stück für Stück abgetragen"
                    className="img-fluid rounded w-100"
                    style={{ objectFit: "cover", maxHeight: "360px" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-white mt-2 fs-6 fst-italic text-center">
                    Spezialbaumfällung von der Hubarbeitsbühne: abgetragen in Teilen
                  </p>
                </Col>
                <Col xs={12} md={7}>
                  <p className="fs-5 text-shadow">
                    Gefahrbäume sind Bäume, von denen eine Gefahr ausgeht: nach Sturm angebrochen,
                    von Pilzen befallen, hohl im Stamm, mit Totholz in der Krone oder einfach zu
                    nah an dem, was sie beim Umfallen treffen würden. Vor jeder Fällung beurteilen
                    wir Standort, Zustand und Fallraum und legen fest, wie der Baum sicher zu Boden kommt.
                  </p>
                  <p className="fs-5 text-shadow mt-3">
                    Bei Sturmschäden zählt oft die Zeit. Angebrochene Stämme und hängende Kronen
                    stehen unter Spannung und lassen sich nicht einfach absägen. Wir sichern solche
                    Bäume zuerst und bauen die Spannung dann schrittweise ab. Das ist auf Wunsch auch kurzfristig möglich.
                  </p>
                </Col>
              </Row>

              <h3 className="ff-article-h3">
                Seilklettertechnik und Arbeitsbühne
              </h3>
              <p className="fs-5 text-shadow">
                Für das Abtragen in Teilen gibt es zwei Wege nach oben. Die Hubarbeitsbühne bringt
                uns schnell und sicher in die Krone, wenn der Untergrund sie trägt und der Zugang
                frei ist. Überall sonst, etwa in engen Gärten, an Hängen, im Bestand oder hinter dem
                Haus, klettern wir mit Seilklettertechnik direkt in den Baum.
              </p>

              <Row className="align-items-start my-4 gy-3">
                <Col xs={12} md={7}>
                  <p className="fs-5 text-shadow">
                    Abgesägte Kronenteile und Stammstücke werden über Umlenkrollen am Seil
                    abgelassen, statt frei zu fallen. So bleiben Zäune, Beete, Dächer und Grabstätten
                    unversehrt, und der Arbeitsbereich am Boden bleibt klein. Wo ein ganzer Stamm
                    gezielt umgezogen werden muss, hilft die Seilwinde der Raupe mit einem Zugseil in
                    die gewünschte Fallrichtung.
                  </p>
                  <p className="fs-5 text-shadow mt-3">
                    Das anfallende Holz schneiden wir auf Wunsch zu Brennholzlängen, häckseln das
                    Astwerk vor Ort oder fahren alles ab. Auch den Stubben entfernen wir: Mit der
                    Stubbenfräse wird der Stumpf unter die Bodenoberfläche gefräst, die Fläche kann
                    anschließend neu bepflanzt oder eingesät werden.
                  </p>
                </Col>
                <Col xs={12} md={5}>
                  <img
                    src={BILD_KLETTERN.src}
                    width={BILD_KLETTERN.breite}
                    height={BILD_KLETTERN.hoehe}
                    alt="Baumkletterer mit Seilklettertechnik beim Abtragen einer Tanne"
                    className="img-fluid rounded w-100"
                    style={{ objectFit: "cover", maxHeight: "360px" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-white mt-2 fs-6 fst-italic text-center">
                    Mit Seilklettertechnik dorthin, wo keine Bühne hinkommt
                  </p>
                </Col>
              </Row>

              <h3 className="ff-article-h3">
                Holzrücken mit Seilwinde und Raupe
              </h3>
              <p className="fs-5 text-shadow">
                Im Wald und auf größeren Grundstücken geht es nach der Fällung weiter: Das Holz muss
                aus dem Bestand. Dafür setzen wir den funkgesteuerten Geräteträger Moritz FR 75 mit
                Forstseilwinde ein. Die Stämme werden am Seil zur Rückegasse gezogen, ohne dass die
                Fläche dazwischen befahren wird; mit einem Bodendruck von rund 0,28 kg/cm² hinterlässt die
                Raupe selbst auf feuchtem Boden kaum Spuren.
              </p>

              <Row className="align-items-start my-4 gy-3">
                <Col xs={12} md={5}>
                  <img
                    src={BILD_RUECKEN.src}
                    width={BILD_RUECKEN.breite}
                    height={BILD_RUECKEN.hoehe}
                    alt="Forstraupe Moritz FR 75 zieht einen Stammabschnitt mit der Seilwinde aus dem Bestand"
                    className="img-fluid rounded w-100"
                    style={{ objectFit: "cover", maxHeight: "360px" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-white mt-2 fs-6 fst-italic text-center">
                    Holzrücken mit Seilwinde: Der Boden zwischen Fällort und Rückegasse wird nicht befahren
                  </p>
                </Col>
                <Col xs={12} md={7}>
                  <p className="fs-5 text-shadow">
                    Bodenschonung ist dabei kein Selbstzweck: Verdichtete Waldböden verlieren ihr
                    Porenvolumen, Wasser staut sich, Wurzeln und Pilzgeflechte sterben ab, und der
                    verbleibende Bestand leidet über Jahre. Deshalb planen wir Rückegassen vorab,
                    nutzen Umlenkrollen statt Umwege und legen Reisigmatten aus, wo sich Befahren nicht
                    vermeiden lässt.
                  </p>
                  <p className="fs-5 text-shadow mt-3">
                    Die kompakte Raupe passt zwischen die stehenden Bäume und kommt auch in enge
                    Gassen, schmale Jungbestände und auf Feuchtflächen, also dorthin, wo ein Forsttraktor
                    nicht hinfahren sollte.
                  </p>
                </Col>
              </Row>

              <h3 className="ff-article-h3">Genehmigung und Zeitpunkt</h3>
              <p className="fs-5 text-shadow">
                Vom 1. März bis zum 30. September dürfen Bäume außerhalb des Waldes nach § 39 BNatSchG
                grundsätzlich nicht gefällt werden; besetzte Nester und Quartiere sind ganzjährig
                geschützt. Viele Städte und Gemeinden haben außerdem eine Baumschutzsatzung, und in
                Schleswig-Holstein sind Knicks gesetzlich geschützt. Ob eine Genehmigung nötig ist und
                wann der richtige Zeitpunkt ist, klären wir mit Ihnen bei der Besichtigung.
              </p>

              <p className="fs-5 text-shadow mt-4">
                Sie haben einen Baum, der weg muss, aber nicht einfach fallen darf? Wir sehen uns
                Baum und Standort vor Ort an und machen Ihnen ein Angebot, das zur Lage passt: ob
                Gefahrbaum nach Sturm, Spezialfällung am Haus oder Holzeinschlag mit Rückung im Wald.
              </p>

              <div className="text-center mt-4">
                <Link to="/kontakt" className="btn-ff-primary" onClick={() => window.scrollTo(0, 0)}>
                  Baumfällung anfragen
                </Link>
              </div>

              <div className="ff-cross-links">
                <p>Weitere Leistungen von Forst &amp; Flur</p>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  <Link to="/baumpflege" className="btn-ff-outline" onClick={() => window.scrollTo(0, 0)}>
                    Baumpflege
                  </Link>
                  <Link to="/mulchen" className="btn-ff-outline" onClick={() => window.scrollTo(0, 0)}>
                    Landschaftspflege
                  </Link>
                </div>
              </div>

            </Col>
          </Row>
        </Container>
      </div>

      {/* Fakten-Karussell */}
      <div className="hero-container text-white">
        <Container className="py-5">
          <Row className="justify-content-center text-center mb-4">
            <Col xs={12} lg={9}>
              <span className="ff-eyebrow">Wissenswertes</span>
              <h2 className="ff-section-title" style={{ marginBottom: ".5rem" }}>Gut zu wissen</h2>
              <p style={{ color: "var(--ff-muted)" }}>
                Fakten rund um Fällung, Seiltechnik und Holzrücken
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col xs={12} lg={9}>
              <Carousel
                interval={karussellIntervall(6000)}
                indicators={true}
                prevLabel="Vorherige Folie"
                nextLabel="Nächste Folie"
                indicatorLabels={facts.map((_, i) => `Folie ${i + 1}`)}
              >
                {facts.map((fact, index) => (
                  <Carousel.Item key={index}>
                    <div
                      className="text-center px-3 px-md-5 pb-5"
                      style={{ minHeight: "240px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                    >
                      <div className="p-4 p-md-5 w-100" style={{ background: "rgba(7,26,16,.65)", borderRadius: "3px", border: "1px solid rgba(159,211,86,.15)" }}>
                        <h3 style={{ fontFamily: "var(--fh)", fontWeight: 800, fontSize: "1.5rem", textTransform: "uppercase", color: "var(--ff-grass)", marginBottom: "1rem" }}>
                          {fact.title}
                        </h3>
                        <p className="fs-5 text-white text-shadow mb-0">{fact.text}</p>
                      </div>
                    </div>
                  </Carousel.Item>
                ))}
              </Carousel>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default InfoBaumfaellung;
